import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, Car, Scale, Banknote, FileSignature, Landmark, MapPinned } from "lucide-react";

const QUEUE_META = [
  { key: "kyc_documents", label: "KYC & Documents", icon: FileCheck2, hint: "Pending / additional documents" },
  { key: "cardr_valuation", label: "CarDr / Valuation", icon: Car, hint: "Report or valuation pending" },
  { key: "underwriting", label: "Underwriting", icon: Scale, hint: "Awaiting credit decision" },
  { key: "enach_setup", label: "eNACH Setup", icon: Banknote, hint: "Mandate not active" },
  { key: "agreement_pending", label: "Agreement Pending", icon: FileSignature, hint: "Sanctioned, agreement not generated" },
  { key: "disbursement", label: "Disbursement", icon: Landmark, hint: "Sanctioned, ready to disburse" },
  { key: "rto_tracker", label: "RTO / Hypothecation", icon: MapPinned, hint: "Disbursed, endorsement pending" },
];

export default function OperationsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("kyc_documents");

  useEffect(() => {
    api.get("/operations/queues").then(r => setData(r.data)).catch(() => toast.error("Could not load operations queues"));
  }, []);

  if (!data) return <div className="text-sm text-slate-500">Loading queues…</div>;

  const items = data.queues[active] || [];
  const activeMeta = QUEUE_META.find(q => q.key === active);

  const ageDays = (iso) => {
    if (!iso) return "—";
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return d <= 0 ? "today" : `${d}d`;
  };

  return (
    <div data-testid="operations-dashboard">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Operations Queues</h1>
      <p className="text-sm text-slate-600 mb-6">Post-approval and pre-disbursement task queues with SLA ageing.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {QUEUE_META.map(q => {
          const Icon = q.icon;
          const count = data.counts[q.key] ?? 0;
          return (
            <Card
              key={q.key}
              className={`p-4 cursor-pointer transition-colors ${active === q.key ? "border-teal-500 bg-teal-50/60" : "border-slate-200 hover:border-slate-300"}`}
              onClick={() => setActive(q.key)}
              data-testid={`ops-queue-${q.key}`}
            >
              <Icon size={16} className={active === q.key ? "text-teal-700" : "text-slate-400"} />
              <div className="text-2xl font-semibold font-mono text-slate-900 mt-1">{count}</div>
              <div className="text-[11px] text-slate-600 leading-tight mt-0.5">{q.label}</div>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 overflow-hidden" data-testid="ops-queue-table">
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="font-semibold text-slate-900 text-sm">{activeMeta?.label}</div>
          <div className="text-xs text-slate-500">{activeMeta?.hint}</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">App ID</th><th className="text-left px-4 py-2">Borrower</th>
              <th className="text-left px-4 py-2">City</th><th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Detail</th><th className="text-left px-4 py-2">Age</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/applications/${it.id}`)} data-testid={`ops-row-${it.application_id}`}>
                <td className="px-4 py-2.5 font-mono text-teal-700">{it.application_id}</td>
                <td className="px-4 py-2.5">{it.borrower || "—"}</td>
                <td className="px-4 py-2.5">{it.city || "—"}</td>
                <td className="px-4 py-2.5 text-xs">{it.status}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">
                  {it.enach_status !== undefined && `eNACH: ${it.enach_status}`}
                  {it.docs_uploaded !== undefined && `${it.docs_uploaded} docs uploaded`}
                  {it.has_pdf !== undefined && (it.has_pdf ? "PDF uploaded" : "No CarDr PDF")}
                  {it.form_34_filed !== undefined && `Form 34: ${it.form_34_filed ? "filed" : "pending"} · Hyp: ${it.hypothecation_endorsed ? "done" : "pending"}`}
                  {it.agreement !== undefined && `Agreement: ${it.agreement ? "yes" : "no"} · eNACH: ${it.enach}`}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{ageDays(it.updated_at)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Queue is clear. 🎉</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
